import React from "react";
import { CgProfile } from "react-icons/cg";

// Helper function to format timestamp (e.g., HH:MM)
const formatMessageTime = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
};

const ChatMessage = ({ 
  message, 
  index, 
  messages, 
  currentUser, 
  animatedVibrationMessages 
}) => {
  const getMessageType = (messageSenderId) => {
    if (!currentUser) {
      return "receiver"; // currentUser yoksa karşı taraf mesajı olarak göster
    }

    // Her iki ID'yi de string'e çevir karşılaştırma için
    const messageId = String(messageSenderId);
    const userId = String(currentUser?.id);
    
    // Backend ID karşılaştırması (eski mesajlar için)
    let messageBackendId = message.backendUserId;
    if (!messageBackendId) {
      // SenderId numeric ise (33 gibi), o backend ID'dir
      if (!isNaN(messageSenderId) && messageSenderId.length < 10) {
        messageBackendId = messageSenderId;
      } else {
        // Socket ID ise fallback
        messageBackendId = messageSenderId; 
      }
    }
    
    const userBackendId = String(currentUser?.backendUserId || currentUser?.id);
    
    // 1. Socket ID ile kontrol
    const isOwnBySocketId = messageId === userId;
    
    // 2. Backend ID ile kontrol (öncelikli)
    const isOwnByBackendId = String(messageBackendId) === userBackendId;
    
    // 3. GÜÇLÜ: İsim kontrolü (email'in @ öncesi kısmı ile)
    const messageSenderName = message.senderName?.toLowerCase().trim();
    const currentUserName = currentUser?.name?.toLowerCase().trim();
    let isOwnBySenderName = false;
    
    if (messageSenderName && currentUserName) {
      // Email kontrolü
      const messageNamePart = messageSenderName.includes('@') ? messageSenderName.split('@')[0] : messageSenderName;
      const currentNamePart = currentUserName.includes('@') ? currentUserName.split('@')[0] : currentUserName;
      
      isOwnBySenderName = messageNamePart === currentNamePart;
      
      // İkinci kontrol: tam isim eşleşmesi
      if (!isOwnBySenderName) {
        isOwnBySenderName = messageSenderName === currentUserName;
      }
    }
    
    // Öncelik sırası: Backend ID > İsim kontrolü > Socket ID
    const isOwn = isOwnByBackendId || isOwnBySenderName || isOwnBySocketId;
    
    // Debug log - sadece yanlış görünen mesajlar için
    if (!isOwn && messageSenderName && currentUserName) {
      const nameMatch = messageSenderName.includes(currentUserName.split('@')[0]) || 
                        currentUserName.includes(messageSenderName.split('@')[0]);
      if (nameMatch) {
        console.log('🚨 SORUNLU MESAJ - Kendi mesajım ama sol tarafta görünüyor:', {
          'mesaj': message.text.substring(0, 30) + '...',
          'mesaj sender': messageSenderName,
          'current user': currentUserName,
          'messageBackendId': messageBackendId,
          'userBackendId': userBackendId,
          'backend ID eşleşmesi': isOwnByBackendId,
          'isim eşleşmesi': isOwnBySenderName,
          'socket ID eşleşmesi': isOwnBySocketId
        });
      }
    }
    
    return isOwn ? "sender" : "receiver";
  };

  const getMessageColor = (messageSenderId) => {
    const messageId = String(messageSenderId);
    const userId = String(currentUser?.id);
    return messageId === userId ? "bg-gray-200" : "bg-pink-100";
  };

  const messageType = getMessageType(message.senderId);
  const messageColor = getMessageColor(message.senderId);
  const messageTime = formatMessageTime(message.timestamp);
  const previousMessage = index > 0 ? messages[index - 1] : null;
  const showSenderInfo =
    !previousMessage || previousMessage.senderId !== message.senderId;
  const isVibrationAnimated = message.type === "vibration" && animatedVibrationMessages[message.id];

  return (
    <div
      className={`flex flex-col ${
        messageType === "sender" ? "items-end" : "items-start"
      } ${showSenderInfo ? "mt-2" : "mt-1"}`}
    >
      {showSenderInfo && (
        <div
          className={`flex items-center gap-2 ${
            messageType === "sender" ? "flex-row-reverse" : "flex-row"
          }`}
        >
        <div className="w-6 h-6 mb-1 rounded-full border border-gray-300 bg-gray-200 flex items-center justify-center"> {message.senderName.toUpperCase().split('')[0]}
          </div>
          {/* <img
            src={message.senderAvatar}
            alt={message.senderName}
            className="w-6 h-6 rounded-full border border-gray-300"
          /> */}
          <span className="text-xs font-semibold text-black font-poppins">
            {message.senderName}
          </span>
        </div>
      )}
      <div
        className={`flex items-end gap-2 ${
          messageType === "sender" ? "flex-row-reverse" : "flex-row"
        }`}
      >
        <div
          className={`${
            message.type === "vibration"
              ? `bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 text-white shadow-lg border-2 border-white ${
                  isVibrationAnimated ? "animate-pulse" : ""
                }`
              : message.type === "emoji"
              ? "bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-white shadow-lg border-2 border-white"
              : messageColor
          } py-2 px-3 rounded-xl max-w-[40ch] text-sm font-poppins ${
            message.type === "vibration" || message.type === "emoji" 
              ? "relative overflow-hidden" 
              : "text-black"
          }`}
          style={
            message.type === "vibration"
              ? {
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
                }
              : message.type === "emoji"
              ? {
                  background:
                    "linear-gradient(135deg, #f093fb 0%, #f5576c 50%, #4facfe 100%)",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
                }
              : {}
          }
        >
          {message.type === "vibration" && isVibrationAnimated && (
            <>
              {/* Havalı sparkle efekt overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-20 animate-pulse"></div>
              {/* Titreşim dalga efekti */}
              <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl blur opacity-30 animate-pulse"></div>
            </>
          )}
          
          {message.type === "emoji" && (
            <>
              {/* Emoji sparkle efekt overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-yellow-200 to-transparent opacity-30 animate-pulse"></div>
              {/* Emoji glow efekti */}
              <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-xl blur opacity-40 animate-pulse"></div>
            </>
          )}
          <div className="relative z-10">
            {message.type === "emoji" ? (
              <div className="flex items-center justify-center">
                <span className="text-4xl">{message.text}</span>
                <span className="ml-2 text-xs text-gray-600">reaction</span>
              </div>
            ) : message.type === "vibration" ? (
              <div className="flex items-center justify-center space-x-2">
                <span
                  className={`text-2xl ${
                    isVibrationAnimated ? "animate-bounce" : ""
                  }`}
                >
                  ⚡
                </span>
                <span className="font-bold text-white drop-shadow-lg">
                  Titreşim
                </span>
                <span
                  className={`text-2xl ${
                    isVibrationAnimated ? "animate-bounce" : ""
                  }`}
                  style={{ animationDelay: "0.1s" }}
                >
                  📳
                </span>
              </div>
            ) : (
              <p className="text-black font-poppins break-words">
                {message.text}
              </p>
            )}
          </div>
        </div>
        {messageTime && (
          <span className="text-xs text-gray-500 opacity-80 font-poppins">
            {messageTime}
          </span>
        )}
      </div>
    </div>
  );
};

export default ChatMessage; 